/* eslint-disable  @nice-cxone/ccf/required-tsdoc*/
import React, { useEffect, useState } from 'react';
import {
  Grid,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Paper,
} from '@mui/material';

import { useDispatch, useSelector } from 'react-redux';
import { MediaType } from '@nice-ccf/core-sdk';
import {
  Contact,
  contactState,
  updateselectedContactHistory,
  contactSelectedIndexState,
  updateContactSelection,
  updateselectedContact,
  updateselectedAgentHistory,
  updateselectedInteractionHistory,
  clearAllSimulatorData,
} from './simulator.slice';
import classes from './simulator.module.scss';
import { BroadcastChannel } from 'broadcast-channel';
import { Helmet } from 'react-helmet';
import {
  CcfAlert,
  CcfBox,
  CcfSnackBar,
  CcfButton,
  CcfTypography,
} from '@nice-ccf/ui-controls';
import {
  getCopilotConfig,
  getIsLiveCopilotEnabled,
  setIsLiveCopilotEnabled,
} from '@nice-ccf/ui-components';
import 'react-datepicker/dist/react-datepicker.css';
import { generateRandomId } from './simulator-util';
import CurrentInteraction from './current-interaction/current-interaction';
import {
  API_BASE_URL,
  callApi,
  PROCESS_UTTERANCE,
} from 'libs/react/ui-components/src/lib/ccf-app-space/ccf-copilot/ccf-copilot-api';
import { v5 as uuidv5, NIL as namespaceNIL } from 'uuid';
import ContactDetailCard from './contact-details-card';
import CopilotConfig from './copilot-config';
import ContactListCard from './contact-list-card';
import CustomerChatData from './customer-chat-data';
import AgentHistoryCard from './agent-history-card';
import CustomerDetailCard from './customer-details-card';
import CopilotDemoSettingsConfig from './copilot-demo-settings-config';

/**
 * Simulator Component to mimic the Call/Post feature from different sources
 */
export function Simulator() {
  //#region Variable region
  const dispatch = useDispatch();
  const contact: Contact[] = useSelector(contactState);
  const selectedContactIndex: number = useSelector(contactSelectedIndexState);
  const selectedContact: Contact = contact[selectedContactIndex];
  const buttonText = selectedContact.media === 'Voice' ? 'Call' : 'Post';
  const [showToast, setShowToast] = useState(false);
  const [openClearSimulator, setOpenClearSimulator] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [clearSimulatorClicked, setClearSimulatorClicked] = useState(false);

  const isLiveCopilotEnabled = useSelector(getIsLiveCopilotEnabled);
  const copilotConfigurationData = useSelector(getCopilotConfig);
  const locale = copilotConfigurationData?.language;
  const namespace = uuidv5('namespace', namespaceNIL);
  const isTestEnvironment =
    window.location.hostname === 'd1s74gzjhz7gu0.cloudfront.net';

  //#endregion

  //#region method region
  /**
   * Used to handle the current contact being selected from the ListCard component
   * @param userIndex - current userIndex
   */
  const handleUserSelection = (userIndex: number) => {
    handleCopilotToggle(false); // Disable Live Copilot simulator View by default on each contact selection
    dispatch(updateselectedContact({ field: 'copilotSource', value: 'JSON' })); // Always update CopilotSource to JSON by default on each contact selection
    dispatch(updateContactSelection(userIndex));
    dispatch(updateselectedContact({ field: 'liveVoice', value: 'No' }));
  };

  useEffect(() => {
    if (
      selectedContact.channel === 'Chat' &&
      selectedContact?.customerChatReply !== ''
    ) {
      broadCastMessage();
    }
  }, [selectedContact?.customerChatReply]);

  /**
   * Used to broadcast the message through 'simulator_channel' using broadcast channel
   */
  const broadCastMessage = async () => {
    let selectedContactCopy = { ...selectedContact };
    selectedContactCopy = { ...selectedContactCopy, dateCreated: new Date() };

    if (selectedContactCopy.channel === 'Email') {
      if (!validateEmail(selectedContactCopy.customer.emailId)) {
        setToastMessage('Entered Email is Invaild');
        setShowToast(true);
      } else {
        const channel: BroadcastChannel<{ type: string; data: Contact }> =
          new BroadcastChannel('simulator_channel');
        await channel.postMessage({
          type: 'insertContact',
          data: selectedContactCopy,
        });
        await channel.close();
        setToastMessage('Your ' + buttonText + ' was successful');
        setShowToast(true);
      }
    } else {
      const channel: BroadcastChannel<{ type: string; data: Contact }> =
        new BroadcastChannel('simulator_channel');
      await channel.postMessage({
        type: 'insertContact',
        data: selectedContactCopy,
      });
      await channel.close();
      setToastMessage('Your ' + buttonText + ' was successful');
      setShowToast(true);
    }
  };

  /**
   * Used to validateEmail
   * @param email - input field values
   */
  const validateEmail = (email: string) => {
    const reg = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
    if (reg.test(email) === false) {
      setToastMessage('Entered Email is Invaild');
      setShowToast(true);
      return false;
    }
    return true;
  };

  /**
   * Used to close the snackbar
   * @param event -React.SyntheticEvent
   * @param reason -string
   */
  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowToast(false);
  };

  /**
   * Used to handle the Form Input change event and update the contact card values
   * @param field - input field name
   * @param value - input field values
   */
  const handleInputChange = (field: string, value: any) => {
    dispatch(updateselectedContact({ field, value }));
    if (field === 'copilotSource') {
      if (value === 'Live LLM') {
        handleCopilotToggle(true);
        if (selectedContact?.botTranscript) {
          dispatch(
            updateselectedContact({ field: 'botTranscript', value: false })
          );
        }
      } else {
        handleCopilotToggle(false);
        dispatch(
          updateselectedContact({
            field: 'customerChatReplyJsonData',
            value: [],
          })
        );
        handleInputChange('liveVoice', 'No');
      }
    }
    if (field === 'liveVoice') {
      const id = generateRandomId('agentName', 'CustomerName');
      handleInputChange('id', id.toString());
    }
  };

  /**
   * Used to broadcast the message through 'simulator_channel' using broadcast channel
   */
  const broadCastCopilotData = (chatMessage: string) => {
    const requestBody = {
      // eslint-disable-next-line @typescript-eslint/no-loss-of-precision
      contactId: contact[selectedContactIndex]?.id,
      busNo: 12345678,
      tenantId: '12345678',
      participantId: 'patron',
      messageBody: chatMessage,
      getSalesOffers: false,
      getCompliance: false,
      getKbCombo: false,
      getProcessSteps: false,
      getConversationAssistance: false,
      messageMetaData: {
        completeUtterance: true,
      },
      transcriptionEventId: uuidv5(chatMessage, namespace),
      threshold_sales: copilotConfigurationData?.threshold_sales ?? 0.75,
      threshold_compliance:
        copilotConfigurationData?.threshold_compliance ?? 0.75,
      agentAssistAppConfig: {
        language: locale?.toLowerCase(),
        generativeResponseTone:
          copilotConfigurationData?.generativeResponseTone ?? 'Formal',
        channels: ['digital', 'voice'],
        transcriptionProfileName: 'google-test',
        enableSentiment: copilotConfigurationData?.enableSentiment ?? true,
        sentimentResponseLength:
          copilotConfigurationData?.sentimentResponseLength ?? 20,
        realTimeSummary: copilotConfigurationData?.realTimeSummary ?? true,
        realTimeSummaryResponseLength:
          copilotConfigurationData?.realTimeSummaryResponseLength ?? 25,
        finalSummary: copilotConfigurationData?.finalSummary ?? true,
        finalSummaryResponseLength:
          copilotConfigurationData?.finalSummaryResponseLength ?? 65,
        behavioralGuidance: true,
        behavioralGuidanceResponseLength: 10,
        behevioralGuidanceNumberOfResponse: 3,
        enableExpertCopilotIntegration: true,
        expertCopilotURL: copilotConfigurationData?.expertCopilotURL ?? '',
        expertCopilotClientKey:
          copilotConfigurationData?.expertCopilotClientKey ?? '',
        expertCopilotClientSecret:
          copilotConfigurationData?.expertCopilotClientSecret ?? '',
        path: copilotConfigurationData?.path ?? '',
        botPersona: copilotConfigurationData?.botPersona ?? '',
        kBAnswersPath: copilotConfigurationData?.kBAnswersPath ?? '',
        salesOffersPath: copilotConfigurationData?.salesOffersPath ?? '',
        compliancePath: copilotConfigurationData?.compliancePath ?? '',
        nextBestResponse: copilotConfigurationData?.nextBestResponse ?? true,
        nextBestResponseLength:
          copilotConfigurationData?.nextBestResponseLength ?? 20,
        nextBestNumberOfResponse:
          copilotConfigurationData?.nextBestNumberOfResponse ?? 2,
        knowledgeBaseArticles:
          copilotConfigurationData?.knowledgeBaseArticles ?? true,
        knowledgeBaseArticlesResponseLength:
          copilotConfigurationData?.knowledgeBaseArticlesResponseLength ?? 65,
        knowledgeBaseWebLinks:
          copilotConfigurationData?.knowledgeBaseWebLinks ?? true,
        knowledgeBaseMaximumWebLinks:
          copilotConfigurationData?.knowledgeBaseMaximumWebLinks ?? 3,
        knowledgeBaseImages:
          copilotConfigurationData?.knowledgeBaseImages ?? true,
        knowledgeBaseMaximumImages:
          copilotConfigurationData?.knowledgeBaseMaximumImages ?? 3,
        knowledgeBaseProcessSteps:
          copilotConfigurationData?.knowledgeBaseProcessSteps ?? true,
        knowledgeBaseMaximumSteps:
          copilotConfigurationData?.knowledgeBaseMaximumSteps ?? 10,
        threshold: copilotConfigurationData?.threshold ?? 0.75,
        vendorId: 'agentCopilot',
        agentAssistId: 'Ved_acp_test123',
        scriptParams: '',
        backAndForthLiveConvo:
          copilotConfigurationData?.backAndForthLiveConvo ?? false,
        backAndForthLiveConvoLength:
          copilotConfigurationData?.backAndForthLiveConvoLength ?? 4,
      },
      messageIsCompletedUtterance: true,
      agentId: 1898,
      mediaType: selectedContact?.media === 'Voice' ? 'Voice' : 'Digital',
      totalAudioChunksInMilliSeconds: 2040,
    };
    return new Promise<void>((resolve, reject) => {
      callApi(API_BASE_URL + PROCESS_UTTERANCE, 'POST', requestBody, dispatch)
        .then((response: any) => {
          resolve();
          const acp_channel: BroadcastChannel<any> = new BroadcastChannel(
            'simulator_channel'
          );
          acp_channel
            .postMessage({
              type: 'process-utterance',
              data: response.data ?? response,
            })
            .then(() => acp_channel.close())
            .catch((error) => {
              console.error(
                'Error while posting message to broadcast channel:',
                error
              );
              reject(error);
            });
        })
        .catch((error: any) => {
          console.error(
            'There was a problem with the fetch operation from simulator:',
            error
          );
          reject(error);
        });
      callApi(
        API_BASE_URL + PROCESS_UTTERANCE,
        'POST',
        { ...requestBody, getKbCombo: true },
        dispatch
      )
        .then((response: any) => {
          resolve();
          //process steps
          const matchingConfidence = selectedContact?.confidence?.find(
            (item: any) =>
              chatMessage.toLowerCase().includes(item.statement.toLowerCase())
          );

          const showGreenConfidenceDot = matchingConfidence
            ? (matchingConfidence as { value: string }).value === 'High'
            : false;
          callApi(
            API_BASE_URL + PROCESS_UTTERANCE,
            'POST',
            { ...requestBody, getProcessSteps: true },
            dispatch
          )
            .then((response: any) => {
              resolve();
              const acp_channel: BroadcastChannel<any> = new BroadcastChannel(
                'simulator_channel'
              );
              acp_channel
                .postMessage({
                  type: 'process-utterance',
                  data: response.data ?? response,
                  ignore: true,
                  showGreenConfidenceDot,
                })
                .then(() => acp_channel.close())
                .catch((error) => {
                  console.error(
                    'Error while posting message to broadcast channel:',
                    error
                  );
                  reject(error);
                });
            })
            .catch((error: any) => {
              console.error(
                'There was a problem with the fetch operation from simulator:',
                error
              );
              reject(error);
            });
          const acp_channel: BroadcastChannel<any> = new BroadcastChannel(
            'simulator_channel'
          );
          acp_channel
            .postMessage({
              type: 'process-utterance',
              data: response.data ?? response,
              showGreenConfidenceDot,
            })
            .then(() => acp_channel.close())
            .catch((error) => {
              console.error(
                'Error while posting message to broadcast channel:',
                error
              );
              reject(error);
            });
        })
        .catch((error: any) => {
          console.error(
            'There was a problem with the fetch operation from simulator:',
            error
          );
          reject(error);
        });

      // sales Offers
      if (copilotConfigurationData?.salesOffers ?? true) {
        callApi(
          API_BASE_URL + PROCESS_UTTERANCE,
          'POST',
          { ...requestBody, getSalesOffers: true },
          dispatch
        )
          .then((response: any) => {
            resolve();
            const acp_channel: BroadcastChannel<any> = new BroadcastChannel(
              'simulator_channel'
            );
            acp_channel
              .postMessage({
                type: 'process-utterance',
                data: response.data ?? response,
              })
              .then(() => acp_channel.close())
              .catch((error) => {
                console.error(
                  'Error while posting message to broadcast channel:',
                  error
                );
                reject(error);
              });
          })
          .catch((error: any) => {
            console.error(
              'There was a problem with the fetch operation from simulator:',
              error
            );
            reject(error);
          });
      }
      // complaince
      if (copilotConfigurationData?.compliance ?? true) {
        callApi(
          API_BASE_URL + PROCESS_UTTERANCE,
          'POST',
          { ...requestBody, getCompliance: true },
          dispatch
        )
          .then((response: any) => {
            resolve();
            const acp_channel: BroadcastChannel<any> = new BroadcastChannel(
              'simulator_channel'
            );
            acp_channel
              .postMessage({
                type: 'process-utterance',
                data: response.data ?? response,
              })
              .then(() => acp_channel.close())
              .catch((error) => {
                console.error(
                  'Error while posting message to broadcast channel:',
                  error
                );
                reject(error);
              });
          })
          .catch((error: any) => {
            console.error(
              'There was a problem with the fetch operation from simulator:',
              error
            );
            reject(error);
          });
      }
      // if(){
      // conversation Assistance
      callApi(
        API_BASE_URL + PROCESS_UTTERANCE,
        'POST',
        { ...requestBody, getConversationAssistance: true },
        dispatch
      )
        .then((response: any) => {
          resolve();
          const acp_channel: BroadcastChannel<any> = new BroadcastChannel(
            'simulator_channel'
          );
          acp_channel
            .postMessage({
              type: 'process-utterance',
              data: response.data ?? response,
            })
            .then(() => acp_channel.close())
            .catch((error) => {
              console.error(
                'Error while posting message to broadcast channel:',
                error
              );
              reject(error);
            });
        })
        // }
        .catch((error: any) => {
          console.error(
            'There was a problem with the fetch operation from simulator:',
            error
          );
          reject(error);
        });
    });
  };

  /**
   * Used to handle the Form Input change event and update the contact card values
   * @param field - input field name
   * @param value - input field values
   */
  const handleInputChangeForAgentHistory = (
    id: any | undefined,
    field: string,
    value: string
  ) => {
    dispatch(updateselectedAgentHistory({ id, field, value }));
  };

  /**
   * Used to handle the Form Input change event and update the contact card values
   * @param field - input field name
   * @param value - input field values
   */
  const handleInputChangeForHistory = (
    id: any | undefined,
    field: string,
    modifedtags: any,
    isFromContactHistory?: boolean
  ) => {
    const isTagsArr = modifedtags.includes(',');
    let value = [];
    if (field === 'tags' && isTagsArr) value = modifedtags.split(',');
    else value.push(modifedtags);
    if (field === 'tags') {
      dispatch(updateselectedContactHistory({ id, field, value }));
    } else {
      dispatch(updateselectedContactHistory({ id, field, value: modifedtags }));
    }
    if (isFromContactHistory && field === 'status') {
      dispatch(
        updateselectedContactHistory({
          id,
          field: 'disposition',
          value: modifedtags === 'Closed' ? modifedtags : 'Open',
        })
      );
    }
  };

  /**
   * Used to handle the Form Input change event and update the contact card values
   * @param field - input field name
   * @param value - input field values
   */
  const handleInputChangeForInteractionHistory = (
    id: any | undefined,
    field: string,
    value: any
  ) => {
    dispatch(updateselectedInteractionHistory({ id, field, value }));
  };

  //#endregion
  const handleCopilotToggle = async (isLiveCopilotEnabled: boolean) => {
    dispatch(setIsLiveCopilotEnabled(isLiveCopilotEnabled));
  };

  /**@example */
  const clearAllSimulator = () => {
    setClearSimulatorClicked(true);
    dispatch(clearAllSimulatorData());
    dispatch(setIsLiveCopilotEnabled(false));
    setToastMessage('The simulator data has been reset successfully.');
    setShowToast(true);
  };

  /**@example */
  const HandleClearSimulatorDialog = ({
    clearAllSimulator,
    openClearSimulator,
    setOpenClearSimulator,
  }: any) => {
    const dispatch = useDispatch();
    /**
     * @example
     */
    const resetDialogBox = () => {
      setOpenClearSimulator(false);
    };

    const handleClearAll = () => {
      clearAllSimulator();
      setOpenClearSimulator(false);
    };
    return (
      <CcfBox>
        <Dialog
          onClose={resetDialogBox}
          open={openClearSimulator}
          fullWidth={false}
          maxWidth="md"
          scroll="paper"
          aria-labelledby="scroll-dialog-title"
          aria-describedby="scroll-dialog-description"
        >
          <DialogContent className={classes.blockContainer}>
            Are you sure you want to reset all the simulator data?
          </DialogContent>
          <DialogActions>
            <CcfButton
              sx={{ border: '1px solid #b0cddb' }}
              onClick={resetDialogBox}
            >
              <CcfTypography>No</CcfTypography>
            </CcfButton>
            <CcfButton
              primary
              onClick={() => {
                handleClearAll();
              }}
            >
              <CcfTypography>Yes</CcfTypography>
            </CcfButton>
          </DialogActions>
        </Dialog>
      </CcfBox>
    );
  };

  return (
    <>
      <Helmet>
        <title>
          {isTestEnvironment
            ? 'CXone Test Agent Simulator'
            : 'CXone Agent Simulator'}
        </title>
      </Helmet>
      <Box className={classes.simContainer}>
        {/* <FormControlLabel
          control={
            <Checkbox
              checked={isLiveCopilotEnabled}
              onChange={handleCopilotToggle}
            />
          }
          label="Copilot Live mode"
          labelPlacement="start"
          style={{
            marginLeft: '0',
            backgroundColor: '#eaf0f6',
            marginRight: '0',
          }}
        /> */}
        <Grid direction="row" container className={classes.simulatorContainer}>
          <ContactListCard
            {...{
              contact,
              selectedContactIndex,
              handleTabSelection: handleUserSelection,
            }}
          />
          <ContactDetailCard
            {...{
              selectedContact,
              handleInputChange,
              buttonText,
              handleButtonClick: broadCastMessage,
            }}
          />
          {!isLiveCopilotEnabled && (
            <CustomerDetailCard
              {...{
                selectedContact,
                handleInputChange,
                clearSimulatorClicked,
                setClearSimulatorClicked,
              }}
            />
          )}

          {!isLiveCopilotEnabled && (
            <CurrentInteraction
              interactionList={selectedContact?.recentInteraction}
              // handleCurrentInteractionChange={handleCurrentInteractionChange}
            />
          )}
          {!isLiveCopilotEnabled && (
            <AgentHistoryCard
              {...{
                selectedContact,
                agentHistoryList: selectedContact.customer.customerHistory,
                handleInputChangeForAgentHistory,
                handleInputChangeForHistory,
                handleInputChangeForInteractionHistory,
              }}
            />
          )}
          {isLiveCopilotEnabled && (
            <CopilotConfig
              {...{
                selectedContact,
                handleButtonClick: broadCastMessage,
                buttonText,
              }}
            />
          )}
          {isLiveCopilotEnabled && (
            <CopilotDemoSettingsConfig
              {...{
                selectedContact,
                handleButtonClick: broadCastMessage,
                buttonText,
                isLiveCopilotEnabled,
                setToastMessage,
                setShowToast,
                handleInputChange,
              }}
            />
          )}
          <Grid
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'flex-end',
              flexDirection: 'column',
            }}
            className={classes.simulatorColumn}
          >
            {((selectedContact.media === MediaType.DIGITAL &&
              selectedContact.channel === 'Chat') ||
              (selectedContact?.media === MediaType.VOICE &&
                selectedContact.copilotAssist === 'Yes')) && (
              <Paper elevation={3} className={classes.simulatorPaper}>
                <CustomerChatData
                  {...{
                    selectedContact,
                    handleInputChange,
                    handleButtonClick: broadCastMessage,
                    broadCastCopilotData: broadCastCopilotData,
                    isLiveCopilotEnabled,
                    toastMessage,
                    setToastMessage,
                    setShowToast,
                    clearSimulatorClicked,
                    setClearSimulatorClicked,
                  }}
                />
              </Paper>
            )}
            <CcfBox
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '20px',
                height: '100%',
                alignItems: 'flex-end',
              }}
            >
              <CcfButton
                primary
                // className={`${classes.btn} ${classes.saveButton}`}
                id={`sm_clearAll_simulator_button`}
                onClick={() => {
                  setOpenClearSimulator(true);
                }}
                style={{
                  margin: '25px 10px',
                  padding: '8px 20px',
                  height: '2.5rem',
                }}
              >
                <CcfTypography>Clear All</CcfTypography>
              </CcfButton>
            </CcfBox>
          </Grid>
          <CcfBox>
            <CcfSnackBar
              open={showToast}
              autoHideDuration={3000}
              onClose={() => handleClose()}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              <div>
                <CcfAlert closeAlert={handleClose} severity="success">
                  {toastMessage}
                </CcfAlert>
              </div>
            </CcfSnackBar>
          </CcfBox>
          <HandleClearSimulatorDialog
            clearAllSimulator={clearAllSimulator}
            openClearSimulator={openClearSimulator}
            setOpenClearSimulator={setOpenClearSimulator}
          />
        </Grid>
      </Box>
    </>
  );
}

export default Simulator;
